// src/components/ProtectedRoute.js
import React from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { Navigate } from 'react-router-dom';
import { Box, Spinner, Text, Alert, AlertIcon, AlertTitle, AlertDescription } from '@chakra-ui/react';
import { routes } from '../config/auth.config';
import { useUserInitialization } from '../hooks/useUserInitialization';

/**
 * Protected Route wrapper that handles authentication and user initialization
 * Shows appropriate loading/error states during auth checks
 */
const ProtectedRoute = ({ component: Component }) => {
  const { isAuthenticated, isLoading: authLoading, error: authError } = useAuth0();
  const { isInitializing, isInitialized, initError } = useUserInitialization();

  // Show loading state while checking authentication
  if (authLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minH="400px" flexDirection="column">
        <Spinner size="xl" color="teal.500" thickness="4px" speed="0.65s" />
        <Text mt={4} color="gray.600">Checking authentication...</Text>
      </Box>
    );
  }

  // Handle authentication errors
  if (authError) {
    return (
      <Box p={8}>
        <Alert status="error" borderRadius="md">
          <AlertIcon />
          <Box>
            <AlertTitle>Authentication Error</AlertTitle>
            <AlertDescription>
              {authError.message || 'An error occurred during authentication. Please try logging in again.'}
            </AlertDescription>
          </Box>
        </Alert>
      </Box>
    );
  }

  // Redirect to login if not authenticated
  if (!isAuthenticated) {
    return <Navigate to={routes.login} replace />;
  }

  // Show loading state while initializing user in database
  if (isInitializing) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minH="400px" flexDirection="column">
        <Spinner size="xl" color="teal.500" thickness="4px" speed="0.65s" />
        <Text mt={4} color="gray.600">Setting up your account...</Text>
      </Box>
    );
  }

  // Handle user initialization errors
  if (initError) {
    return (
      <Box p={8}>
        <Alert status="error" borderRadius="md">
          <AlertIcon />
          <Box>
            <AlertTitle>Account Setup Error</AlertTitle>
            <AlertDescription>
              {initError}. Please refresh the page or contact support if the problem persists.
            </AlertDescription>
          </Box>
        </Alert>
      </Box>
    );
  }

  // Render the protected component
  return <Component />;
};

export default ProtectedRoute;
